import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Col, Form, Input, Row } from 'antd';
import Icon from 'assets/Icons';
import Button from 'components/Button';
import CurrencyInputPrecision from 'components/CurrencyInputPrecision';
import Modal from 'components/Modal';
import Text from 'components/Text';
import styled from 'styled-components';
import { IServiceItemData } from 'services/shop/types/categories';
import settingApis from 'features/settingService/services/apis';
import { IResponseDataBody } from 'services/response';
import toast from 'utils/toast';
import { ICreateOrUpdateServiceBodyType } from 'features/settingService/services/types/api';
import { get, sumBy } from 'lodash';
import ChildrenIdsInput from './ChildrenIdsInput';
import SelectMultipleServicesModal, { useSelectMultipleServicesModalRef } from 'components/SelectMultipleServicesModal';
import ModalAddNewServices, { useModalAddNewServicesRef } from '../ServicesSettingV2/ModalAddNewServices';
import ModalAddMultiNewServices, { useModalAddMultiNewServicesRef } from '../ServicesSettingV2/ModalAddMultiNewServices';
import AddOnsInput from './AddOnsInput';

type Props = {};

export interface IUpdateServiceModalRef {
  open: (data: IServiceItemData, success?: () => void) => void;
}

export const useUpdateServiceModalRef = () => useRef<IUpdateServiceModalRef>(null);

const UpdateServiceModal = forwardRef<IUpdateServiceModalRef, Props>(({}, ref) => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<IServiceItemData | null>(null);
  const [form] = Form.useForm<IServiceItemData>();
  const successRef = useRef<() => void>();
  const selectMultipleServicesModalRef = useSelectMultipleServicesModalRef();
  const modalAddNewServicesRef = useModalAddNewServicesRef();
  const modalAddMultiNewServicesRef = useModalAddMultiNewServicesRef();

  const handleCloseModal = () => {
    setOpen(false);
    setData(null);
    form.resetFields();
  };

  const handleSubmitModal = () => {
    form.submit();
  };

  const onOpenModal = (data: IServiceItemData, success?: () => void) => {
    setOpen(true);
    setData(data);
    form.setFieldsValue(data);
    successRef.current = success;
  };

  useImperativeHandle(ref, () => ({
    open: (data: IServiceItemData, success?: () => void) => {
      onOpenModal(data, success);
    },
  }));

  const onFinish = async (values: IServiceItemData) => {
    try {
      setLoading(true);
      const body: ICreateOrUpdateServiceBodyType = {
        id: data?.id || '',
        categoryId: data?.catId || '',
        serviceId: data?.id || '',
        orderNumber: values.orderNumber || 0,
        serviceName: values.serviceName,
        priceSell: values.priceSell,
        duration: values.duration,
        supplyFee: values.supplyFee || 0,
        urlVideo: values.urlVideo || '',
        description: values.description || '',
        childrenIds: values.childServices?.map((item: IServiceItemData) => item.id) || [],
        addOnIds: values.addOnServices?.map((item: IServiceItemData) => item.id) || [],
      };
      const res: IResponseDataBody<boolean> = await settingApis.createOrUpdateService(body);
      if (res?.data?.data) {
        toast.success('Service updated successfully');
        successRef.current?.();
        handleCloseModal();
      } else {
        toast.error('Service update failed');
      }
    } catch (error) {
      const msg = get(error, 'response.data.message');
      toast.error(msg || 'Service update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAddServices = async () => {
    const currentChildServices = form.getFieldValue('childServices') || [];

    const selectedServices = await selectMultipleServicesModalRef.current?.open({
      selectedCategoryId: '',
      selectedServiceIds: currentChildServices?.map((item: IServiceItemData) => item.id) || [],
      baseServices: currentChildServices,
    });

    if (selectedServices) {
      form.setFieldsValue({ childServices: selectedServices });
      const totalPriceSell = sumBy(selectedServices, (item: IServiceItemData) => item.priceSell);
      form.setFieldValue(['priceSell'], totalPriceSell);
    }
  };

  const handleAddNewServices = async () => {
    const newService = await modalAddNewServicesRef.current?.open();

    if (newService) {
      const currentChildServices = form.getFieldValue('childServices') || [];
      const updatedChildServices = [...currentChildServices, newService];
      form.setFieldsValue({ childServices: updatedChildServices });
      const totalPriceSell = sumBy(updatedChildServices, (item: IServiceItemData) => item.priceSell);
      form.setFieldValue(['priceSell'], totalPriceSell);
    }
  };

  const handleAddMultiNewServices = async () => {
    const newServices = await modalAddMultiNewServicesRef.current?.open();

    if (newServices && newServices.length > 0) {
      const currentAddOnServices = form.getFieldValue('addOnServices') || [];
      const updatedAddOnServices = [...currentAddOnServices, ...newServices];
      form.setFieldsValue({ addOnServices: updatedAddOnServices });
    }
  };

  const handleAddServicesAddOn = async () => {
    const currentAddOnServices = form.getFieldValue('addOnServices') || [];

    const selectedServices = await selectMultipleServicesModalRef.current?.open({
      selectedCategoryId: '',
      selectedServiceIds: currentAddOnServices?.map((item: IServiceItemData) => item.id) || [],
      baseServices: currentAddOnServices,
    });

    if (selectedServices) {
      form.setFieldsValue({ addOnServices: selectedServices });
    } 
  };

  return (
    <Modal
      modalTitle='UPDATE SERVICE'
      visible={open}
      onClose={handleCloseModal}
      onSubmit={handleSubmitModal}
      okTitle='OK'
      cancelTitle='Close'
      width={'1200px'}
      noneBodyStyle
      noneFooterStyle
      disableOk={loading}
      v2
    >
      <FormLayoutStyled form={form} onFinish={onFinish}>
        <Row gutter={[24, 16]}>
          <Col span={8}>
            <FormFieldWrapper>
              <Text variant='CONTENT_1' color='text_3'>
                Ordinal Number
              </Text>
              <Form.Item
                name='orderNumber'
                rules={[
                  { required: true, message: 'Missing Ordinal Number' },
                  {
                    validator: async (_, value) => {
                      if (value > 0) return;
                      throw new Error('Number must be greater than 0');
                    },
                  },
                ]}
              >
                <Input type='number' />
              </Form.Item>
            </FormFieldWrapper>
          </Col>

          <Col span={8}>
            <FormFieldWrapper>
              <Text variant='CONTENT_1' color='text_3'>
                Service Name
              </Text>
              <Form.Item name='serviceName' rules={[{ required: true, message: 'Missing service name' }]}>
                <Input tabIndex={1} placeholder='e.g. Waxing' />
              </Form.Item>
            </FormFieldWrapper>
          </Col>

          <Col span={8}>
            <FormFieldWrapper>
              <Text variant='CONTENT_1' color='text_3'>
                Sell Price
              </Text>
              <Form.Item
                name='priceSell'
                rules={[
                  { required: true, message: 'Missing priceSell' },
                  {
                    validator: async (_, value) => {
                      if (value >= 0) return;
                      throw new Error('Number must be greater than 0');
                    },
                  },
                ]}
              >
                <CurrencyInputPrecision tabIndex={2} size='small'/>
              </Form.Item>
            </FormFieldWrapper>
          </Col>

          <Col span={8}>
            <FormFieldWrapper>
              <Text variant='CONTENT_1' color='text_3'>
                Duration (Minutes)
              </Text>
              <Form.Item
                name='duration'
                rules={[
                  { required: true, message: 'Missing duration' },
                  {
                    validator: async (_, value) => {
                      if (value >= 0) return;
                      throw new Error('Number must be greater than 0');
                    },
                  },
                ]}
              >
                <Input type='number' tabIndex={4} />
              </Form.Item>
            </FormFieldWrapper>
          </Col>

          <Col span={8}>
            <FormFieldWrapper>
              <Text variant='CONTENT_1' color='text_3'>
                Supply Fee
              </Text>
              <Form.Item name='supplyFee'>
                <CurrencyInputPrecision tabIndex={3} size='small'/>
              </Form.Item>
            </FormFieldWrapper>
          </Col>

          <Col span={8}>
            <FormFieldWrapper>
              <Text variant='CONTENT_1' color='text_3'>
                Input URL
              </Text>
              <Form.Item name='urlVideo'>
                <Input tabIndex={6} placeholder='https://...' prefix={<Icon type='link' />} size='small'/>
              </Form.Item>
            </FormFieldWrapper>
          </Col>

          <Col span={24}>
            <FormFieldWrapper>
              <Text variant='CONTENT_1' color='text_3'>
                Description
              </Text>
              <Form.Item name='description'>
                <Input.TextArea rows={4} placeholder='Enter service description...' tabIndex={5} />
              </Form.Item>
            </FormFieldWrapper>
          </Col>
          <Col span={24}>
            <Form.Item name='childServices'>
              <ChildrenIdsInput onAddServices={handleAddServices} isShowAddNewServices={true} onAddNewServices={handleAddNewServices} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name='addOnServices'>
              <AddOnsInput 
                onAddServices={handleAddServicesAddOn} 
                isShowAddNewServices={true} 
                onAddNewServices={handleAddMultiNewServices} 
              />
            </Form.Item>
          </Col>
        </Row>
        {/* <ButtonGroupStyled>
          <Button ntype='SECONDARY' block onClick={handleCloseModal}>
            Close
          </Button>
          <Button ntype='PRIMARY' block onClick={handleSubmitModal} disabled={loading}>
            Save
          </Button>
        </ButtonGroupStyled> */}
      </FormLayoutStyled>
      <SelectMultipleServicesModal ref={selectMultipleServicesModalRef} />
      <ModalAddNewServices ref={modalAddNewServicesRef} />
      <ModalAddMultiNewServices ref={modalAddMultiNewServicesRef} />
    </Modal>
  );
});

UpdateServiceModal.displayName = 'UpdateServiceModal';

export default UpdateServiceModal;

const FormLayoutStyled = styled(Form<IServiceItemData>)`
  padding: 1.5rem;
  .ant-input-affix-wrapper {
    .ant-input-prefix {
      display: flex;
      align-items: center;
      margin-right: 0.5rem;
    }
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-input-number,
  textarea.ant-input {
    border-color: rgba(0, 0, 0, 0.15) !important;

    &:hover {
      border-color: rgba(0, 0, 0, 0.25) !important;
    }

    &:focus,
    &.ant-input-focused,
    &.ant-input-affix-wrapper-focused {
      border-color: rgba(0, 0, 0, 0.3) !important;
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.06) !important;
    }
  }

  .ant-form-item {
    margin-bottom: 0;
  }
`;

const FormFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const ButtonGroupStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;
